import React from 'react';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import classes from 'classnames';
import sanitizeHtml from 'sanitize-html';
import './block-answer.scss';
import { Legend } from '../Legend';
import { Heading2 } from '../design-system/headings';
import { TextDefault, TextLabel } from '../design-system/texts';
import { palette } from '../design-system/colors';

export const BlockAnswer = observer(
    ({
        blockAnswers,
        hideBlock = false,
        question,
        calculateColor,
        legendStepCount,
        blockType,
        additionalText,
    }) => {
        const { t } = useTranslation('assessment');

        const scorePercentage =
            blockAnswers && blockAnswers.userAnswer
                ? blockAnswers.userAnswer.resolution.percentage
                : 0;

        if (blockAnswers && blockAnswers.answerText) {
            const description = blockAnswers.answerText.description
                ? sanitizeHtml(blockAnswers.answerText.description, {
                      allowedTags: ['strong', 'p'],
                  })
                : blockAnswers.answerText.description_key;

            let correctQuestionCount = 0;
            if (blockAnswers.correctAnswer) {
                if (blockType !== 'groupOfBlocks') {
                    Object.keys(blockAnswers.correctAnswer).forEach(
                        (optionId) => {
                            if (
                                blockAnswers.userAnswer[optionId] &&
                                blockAnswers.userAnswer[optionId].value ===
                                    blockAnswers.correctAnswer[optionId].value
                            ) {
                                correctQuestionCount++;
                            }
                        }
                    );
                } else {
                    /** For the 'groupOfBlocks' block type, we need to drill down one extra level from the group -> groupId -> optionId
                     * Example:
                     *
                     * correctAnswer: {
                     *   "check_for_updates": {
                     *     "once_a_day": {
                     *       "value": "once_a_day",
                     *       "score": 100
                     *     }
                     *   },
                     *   ...
                     * }
                     *
                     * userAnswer: {
                     *   "check_for_updates": {
                     *     "action_id": "updates_center_form[check_for_updates]",
                     *     "question_id": 63,
                     *     "value": "once_a_day",
                     *     "score": 100,
                     *     "details": {}
                     *   },
                     *   ...
                     * }*/
                    Object.keys(blockAnswers.correctAnswer).forEach(
                        (groupId) => {
                            Object.keys(
                                blockAnswers.correctAnswer[groupId]
                            ).forEach((optionId) => {
                                if (
                                    blockAnswers.userAnswer[groupId] &&
                                    blockAnswers.userAnswer[groupId].value ===
                                        blockAnswers.correctAnswer[groupId][
                                            optionId
                                        ].value
                                ) {
                                    correctQuestionCount++;
                                }
                            });
                        }
                    );
                }
            }

            let headingText =
                blockAnswers.answerText.heading ||
                (blockAnswers.userAnswer &&
                    blockAnswers.userAnswer.resolution.heading) ||
                '';
            let answerResolutionHeading;
            if (
                /**Display the '0∕2 correct answers.' type answer heading */
                headingText === 'correct_answers' &&
                blockAnswers.correctAnswer
            ) {
                answerResolutionHeading = t('correct_answers', {
                    count: `${correctQuestionCount}∕${
                        Object.keys(blockAnswers.correctAnswer).length
                    }`,
                });
            } else if (
                /** Display the '0∕2 correct answers and 1 wrong.' type answer heading if there is a wrong selection involved */
                headingText === 'correct_and_wrong_answers' &&
                blockAnswers.correctAnswer &&
                blockAnswers.userAnswer
            ) {
                /** blockAnswers.userAnswer can contain 'actions' and 'resolution' properties,
                 * we only want the answers that have a value associated with them. */
                let userWrongAnswerCount = Object.keys(blockAnswers.userAnswer)
                    .filter((key) => !!blockAnswers.userAnswer[key].value)
                    .filter(
                        (key) => key !== 'select_none'
                    ) /** A special case, for 'Conversation' question. So we don't say '0∕2 correct answers and 1 wrong.' when user selected none of the options but say '0∕2 correct answers.' instead. */
                    .filter(
                        (x) =>
                            !Object.keys(blockAnswers.correctAnswer).includes(x)
                    ).length;

                if (userWrongAnswerCount > 0) {
                    answerResolutionHeading = t('correct_and_wrong_answers', {
                        count: `${correctQuestionCount}∕${
                            Object.keys(blockAnswers.correctAnswer).length
                        }`,
                        wrongCount: userWrongAnswerCount,
                    });
                } else {
                    answerResolutionHeading = t('correct_answers', {
                        count: `${correctQuestionCount}∕${
                            Object.keys(blockAnswers.correctAnswer).length
                        }`,
                    });
                }
            } else {
                answerResolutionHeading = t(headingText);
            }

            /** Special case for strings that include {details.missing}, replace that with translated details */
            if (headingText && headingText.includes('{details.missing}')) {
                answerResolutionHeading = headingText.substring(
                    0,
                    headingText.length - ' {details.missing}.'.length
                );
                answerResolutionHeading = t(answerResolutionHeading);

                if (blockAnswers.userAnswer) {
                    const missingArray =
                        blockAnswers.userAnswer.details.missing;

                    let missingString =
                        formatArrayToStringWithTranslation(missingArray);

                    answerResolutionHeading += ' ' + missingString;
                }
            }

            // Create the string 'lowercase letters, numbers, or special characters'
            function formatArrayToStringWithTranslation(arr) {
                if (arr.length === 0) {
                    return '';
                } else if (arr.length === 1) {
                    return t(arr[0]);
                } else {
                    const lastItem = arr.pop(); // Remove and store the last item
                    const formattedArray = arr.map((item) => t(item));
                    const formattedString =
                        formattedArray.join(', ') +
                        ' ' +
                        t('or') +
                        ' ' +
                        t(lastItem);
                    return formattedString;
                }
            }

            return (
                <div
                    className={classes('question-answer-block', {
                        'hide-arrow': hideBlock,
                    })}
                >
                    <Legend stepCount={legendStepCount} />
                    <Heading2 bold my={8}>
                        <span
                            style={{
                                color: calculateColor
                                    ? calculateColor(scorePercentage)
                                    : '#054',
                            }}
                        >
                            {blockAnswers.userAnswer &&
                                (t(blockAnswers.userAnswer.resolution.text) ||
                                    t(
                                        blockAnswers.userAnswer.resolution
                                            .prefix
                                    ))}
                        </span>
                        {answerResolutionHeading}
                    </Heading2>

                    {!hideBlock &&
                        blockAnswers.userAnswer && ( //Don't show the action list when showing answers with the 'password' presentation
                            <div className="question-answer-actions">
                                {blockAnswers.userAnswer.actions &&
                                    blockAnswers.userAnswer.actions.length >
                                        0 && (
                                        <TextLabel bold>
                                            {t('ACTIONS')}
                                        </TextLabel>
                                    )}
                                {blockAnswers.userAnswer.actions &&
                                    blockAnswers.userAnswer.actions.length >
                                        0 &&
                                    blockAnswers.userAnswer.actions.map(
                                        (action, i) => {
                                            return (
                                                action && (
                                                    <div
                                                        className={classes(
                                                            'action',
                                                            {
                                                                incorrect:
                                                                    action.type ===
                                                                    'incorrect',
                                                            },
                                                            {
                                                                correct:
                                                                    action.type ===
                                                                    'correct',
                                                            }
                                                        )}
                                                        key={'action-' + i}
                                                    >
                                                        {t(action.text)}{' '}
                                                    </div>
                                                )
                                            );
                                        }
                                    )}
                            </div>
                        )}
                    {blockAnswers.answerText.details && (
                        <div className="question-answer-actions">
                            <TextLabel bold className="details-header">
                                {t(blockAnswers.answerText.details.header)}
                            </TextLabel>
                            <ol>
                                {blockAnswers.answerText.details.items.map(
                                    (item, i) => {
                                        return (
                                            <li
                                                className={classes('action')}
                                                key={'option-' + i}
                                            >
                                                {t(item.text)}
                                                <span>
                                                    {t(item.description)}
                                                </span>
                                            </li>
                                        );
                                    }
                                )}
                            </ol>
                        </div>
                    )}
                    {blockAnswers.passwordCriteria && (
                        <div className="question-answer-actions">
                            <TextLabel bold>{t('CRITERIA')}</TextLabel>
                            {blockAnswers.passwordCriteria.map(
                                (criteria, i) => {
                                    return (
                                        <div
                                            className={classes(
                                                'action',
                                                {
                                                    incorrect:
                                                        !criteria.contains,
                                                },
                                                { correct: criteria.contains }
                                            )}
                                            key={'criteria-' + i}
                                        >
                                            {t(criteria.text)}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    )}
                    <Trans
                        defaults={
                            question &&
                            question.personalizedKeysActions &&
                            question.personalizedKeysActions.some(
                                (key) => key.action === 'mutateEmail'
                            )
                                ? t(blockAnswers.answerText.description_key, {
                                      fakeEmailAddress:
                                          question.ui.presentation.email
                                              .from_address,
                                  })
                                : t(blockAnswers.answerText.description_key) ||
                                  t(description)
                        }
                        components={{
                            strong: <strong />,
                            b: <strong />,
                            p: (
                                <TextDefault
                                    mt={8}
                                    color={palette.lightroom.heavyMetalLight}
                                />
                            ),
                        }}
                    />
                    {additionalText && (
                        <Trans
                            defaults={t(additionalText)}
                            components={{
                                strong: <strong />,
                                b: <strong />,
                                p: (
                                    <TextDefault
                                        mt={8}
                                        color={
                                            palette.lightroom.heavyMetalLight
                                        }
                                    />
                                ),
                            }}
                        />
                    )}
                </div>
            );
        } else {
            return null;
        }
    }
);
